import { useState } from 'react';
import { Button, Input } from '@tutero/ui';

import { updateAccountName, type Account } from '@/lib/account';

import {
  PICTURE_MEGABYTES,
  ProfilePictureSection,
} from '../profile-picture/ProfilePictureSection';
import { Field, SettingsSection } from './SettingsSection';
import { reasonFor, useAccountWrite } from './useAccountWrite';

/**
 * The account's name (`settings-is-a-dialog` D3). One field, because the account holds one
 * name: Flutter's first and last are the Firestore `AdminUser` it wrote, and splitting one
 * string to fill two loses a middle name, a double-barrelled surname and every order that
 * is not given-name-first. The staff member's photo stands beside the field, so the two read
 * as one row at the head of Settings.
 */
export function AccountNameSection({ account }: { account: Account }) {
  const [name, setName] = useState(account.name);
  const [done, setDone] = useState(false);
  const save = useAccountWrite(updateAccountName);

  const named = name.trim() !== '';
  const moved = name.trim() !== account.name.trim();

  return (
    <SettingsSection
      title="Your name"
      titleHidden
      error={save.isError ? reasonFor(save.error) : null}
      done={done ? 'Your name was saved.' : null}
      onSubmit={() => {
        if (!named || save.isPending) return;
        setDone(false);
        save.mutate(name, { onSuccess: () => setDone(true) });
      }}
      footer={
        <Button type="submit" disabled={!named || !moved || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save Name'}
        </Button>
      }
    >
      <ProfilePictureSection>
        <Field
          id="settings-name"
          label="Name"
          labelClassName="text-bold-m"
          about={`What the admin and every Tutero surface calls you, and the photo you are drawn with in place of your initial: PNG, JPEG, WebP or GIF, up to ${PICTURE_MEGABYTES} MB.`}
        >
          <Input
            id="settings-name"
            value={name}
            autoComplete="name"
            onChange={(event) => {
              setName(event.target.value);
              setDone(false);
            }}
          />
        </Field>
      </ProfilePictureSection>
      {named ? null : (
        <p role="alert" className="text-destructive text-regular-s">
          A name cannot be blank.
        </p>
      )}
    </SettingsSection>
  );
}
